import React from "react";
import { Box, Grid, Skeleton } from "@mui/material";

const NewsPageSkeleton = ({ isMediumScreen }) => {
  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          m: isMediumScreen ? 3.5 : "15px",
          mt: 0,
          pt: 6,
          ml: isMediumScreen ? 3.5 : "38px",
          borderRadius: "12px",
          mb: 2,
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {/* Hero Image Skeleton */}
        <Skeleton
          variant="rectangular"
          width="100%"
          height={isMediumScreen ? 530 : 225}
          sx={{
            borderRadius: "12px",
            mb: 2,
            bgcolor: "rgba(255, 255, 255, 0.1)",
          }}
        />

        {/* News Title Skeleton */}
        <Box sx={{ mb: 4 }}>
          <Skeleton
            variant="text"
            width={120}
            height={40}
            sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
          />
        </Box>

        {/* Tutorial Cards Grid Skeleton */}
        <Box mb={10}>
          <Grid
            container
            pb={isMediumScreen ? 24 : 14}
            spacing={3}
            justifyContent="start"
          >
            {[1, 2, 3, 4, 5, 6].map((item) => (
              <Grid item key={item} xs={12} sm={6} lg={6} xl={4}>
                <Box
                  sx={{
                    maxWidth: "98%",
                    bgcolor: "#171717",
                    borderRadius: "12px",
                    overflow: "hidden",
                  }}
                >
                  {/* Card Image Skeleton */}
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={200}
                    sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                  />

                  <Box sx={{ p: 3, mb: 1 }}>
                    {/* Category Badge Skeleton */}
                    <Skeleton
                      variant="rectangular"
                      width={110}
                      height={36}
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "99px",
                        mb: 2,
                      }}
                    />

                    {/* Title Skeleton */}
                    <Skeleton
                      variant="text"
                      width="80%"
                      height={32}
                      sx={{ bgcolor: "rgba(255, 255, 255, 0.1)", mb: 1 }}
                    />

                    {/* Description Skeleton */}
                    <Box sx={{ mb: 3 }}>
                      <Skeleton
                        variant="text"
                        width="100%"
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                      />
                      <Skeleton
                        variant="text"
                        width="90%"
                        sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                      />
                    </Box>

                    {/* Button Skeleton */}
                    <Skeleton
                      variant="rectangular"
                      width={isMediumScreen ? "100%" : 200}
                      height={isMediumScreen ? 40 : 48}
                      sx={{
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        borderRadius: "8px",
                      }}
                    />
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>
      {/* Right side - fixed content */}
      {!isMediumScreen && (
        <Box
          sx={{
            width: { lg: 370, sm: 300 },
            p: 3,
            height: "auto",
            overflowY: "auto",
          }}
        >
          {/* UserProfile Skeleton */}
          <Box
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.05)",
              mt: 3,
              p: 3,
              borderRadius: "16px",
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}
            >
              <Skeleton
                variant="circular"
                width={57}
                height={57}
                sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
              />
              <Box>
                <Skeleton
                  variant="text"
                  width={120}
                  sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                />
                <Skeleton
                  variant="text"
                  width={80}
                  sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                />
              </Box>
            </Box>

            <Skeleton
              variant="rectangular"
              height={50}
              sx={{
                my: 2,
                borderRadius: "12px",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              }}
            />

            <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
              <Skeleton
                variant="rectangular"
                width="50%"
                height={40}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }}
              />
              <Skeleton
                variant="rectangular"
                width="50%"
                height={40}
                sx={{
                  borderRadius: "8px",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                }}
              />
            </Box>

            <Skeleton
              variant="rectangular"
              width="100%"
              height={40}
              sx={{
                borderRadius: "8px",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              }}
            />
          </Box>

          {/* Chat Section Skeleton */}
          <Box mt={4}>
            <Box
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.05)",
                borderRadius: "12px",
                pb: 3,
              }}
            >
              {/* Chat Header */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  background: "rgba(255, 255, 255, 0.07)",
                  borderTopRightRadius: "12px",
                  borderTopLeftRadius: "12px",
                  p: 1.5,
                  pl: 2.5,
                  pr: 2.5,
                  mb: 2,
                }}
              >
                <Box display="flex">
                  <Skeleton
                    variant="circular"
                    width={40}
                    height={40}
                    sx={{
                      mr: 1.5,
                      backgroundColor: "rgba(255, 255, 255, 0.1)",
                    }}
                  />
                  <Box>
                    <Skeleton
                      variant="text"
                      width={80}
                      sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                    />
                    <Skeleton
                      variant="text"
                      width={100}
                      sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                    />
                  </Box>
                </Box>
                <Skeleton
                  variant="circular"
                  width={40}
                  height={40}
                  sx={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
                />
              </Box>

              {/* Chat Message */}
              <Box mx={2.5}>
                <Skeleton
                  variant="rectangular"
                  width={220}
                  height={80}
                  sx={{
                    borderRadius: "12px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    mb: 3,
                  }}
                />
              </Box>

              {/* Chat Input */}
              <Box mx={2.5}>
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={40}
                  sx={{
                    borderRadius: "4px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Match Finder Button Skeleton */}
          <Box display="flex" justifyContent="left" mt={4}>
            <Skeleton
              variant="rectangular"
              width={400}
              height={120}
              sx={{
                borderRadius: "12px",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default NewsPageSkeleton;
